import { Injectable } from '@angular/core';
import { ClientInfoDTO, TaskCreateDTO, TaskFormValues } from '../create-task-modal.component';
import { from, Observable, switchMap, throwError } from 'rxjs';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { AuthService } from 'src/app/auth/auth.service';

const API_ENDPOINTS = {
  TASKS: '/tasks',
  TASK_BY_ID: (id: string) => `/tasks/${id}`,
  FLEET_CLIENTS_SEARCH: (query: string) => `/fleet-clients/${query}/search`,
} as const;

const ERROR_MESSAGES = {
  NO_TOKEN: 'No token available',
} as const;

export interface TaskResponseDTO {
  id: string;
  name: string;
  description: string;
  vehicleDescription: string;
  licensePlate: string;
  clientGuid: string;
  clientName?: string;
  realizationDate: string;
  expirationDate: string;
}

@Injectable({
  providedIn: 'root'
})
export class CreateTaskService {
  private readonly baseUrl = environment.cafler_backoffice_back_url;

  constructor(
    private readonly http: HttpClient,
    private readonly authService: AuthService
  ) { }

  createTask(body: TaskCreateDTO): Observable<void> {
    return this.executeAuthenticatedPost<void>(API_ENDPOINTS.TASKS, body);
  }

  updateTask(taskId: string, body: TaskCreateDTO): Observable<void> {
    return this.executeAuthenticatedPut<void>(API_ENDPOINTS.TASK_BY_ID(taskId), body);
  }

  deleteTask(taskId: string): Observable<void> {
    return this.executeAuthenticatedDelete<void>(API_ENDPOINTS.TASK_BY_ID(taskId));
  }

  getTaskById(taskId: string): Observable<TaskResponseDTO> {
    return this.executeAuthenticatedGet<TaskResponseDTO>(API_ENDPOINTS.TASK_BY_ID(taskId));
  }

  searchVehicle(search: string): Observable<ClientInfoDTO[]> {
    return this.executeAuthenticatedGet<ClientInfoDTO[]>(API_ENDPOINTS.FLEET_CLIENTS_SEARCH(search));
  }

  private executeAuthenticatedGet<T>(endpoint: string): Observable<T> {
    return from(this.authService.getAccessToken()).pipe(
      switchMap((token) => {
        if (!token) return throwError(() => new Error(ERROR_MESSAGES.NO_TOKEN));
        const headers = this.buildAuthHeaders(token);
        return this.http.get<T>(`${this.baseUrl}${endpoint}`, { headers });
      })
    );
  }

  private executeAuthenticatedPost<T>(endpoint: string, body: unknown): Observable<T> {
    return from(this.authService.getAccessToken()).pipe(
      switchMap((token) => {
        if (!token) return throwError(() => new Error(ERROR_MESSAGES.NO_TOKEN));
        const headers = this.buildAuthHeaders(token);
        return this.http.post<T>(`${this.baseUrl}${endpoint}`, body, { headers });
      })
    );
  }

  private executeAuthenticatedPut<T>(endpoint: string, body: unknown): Observable<T> {
    return from(this.authService.getAccessToken()).pipe(
      switchMap((token) => {
        if (!token) return throwError(() => new Error(ERROR_MESSAGES.NO_TOKEN));
        const headers = this.buildAuthHeaders(token);
        return this.http.put<T>(`${this.baseUrl}${endpoint}`, body, { headers });
      })
    );
  }

  private executeAuthenticatedDelete<T>(endpoint: string): Observable<T> {
    return from(this.authService.getAccessToken()).pipe(
      switchMap((token) => {
        if (!token) return throwError(() => new Error(ERROR_MESSAGES.NO_TOKEN));
        const headers = this.buildAuthHeaders(token);
        return this.http.delete<T>(`${this.baseUrl}${endpoint}`, { headers });
      })
    );
  }

  private buildAuthHeaders(token: string): HttpHeaders {
    return new HttpHeaders({
      Authorization: `Bearer ${token}`,
    });
  }
}
